import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, capitalize, Checkbox } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import * as S from 'commons/components/Table/Table.styles';
import { IStockTransfer } from 'commons/types';
import {
  DefaultSortProps,
  RowTitleProps,
  SortOrder,
} from 'commons/types/listPageData.interface';
import { StockTransferDataTableProps } from 'commons/types/stockTransferItemData.interface';
import { TableActionRow, DataTable } from 'pages/dashboard/commons/components';
import {
  IOpenBulkDialogData,
  IOpenDialogData,
} from 'pages/dashboard/order-lists/type/open-dialog-state.interface';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import { OpenBulkDialogConfig } from '../configs/open-dialog.config';

import { ActionBtnMenuStockTransfer } from './ActionBtnMenuStockTransfer.component';
import { StockTransferDetailDialog } from './StockTransferDetailDialog.component';

const rowTitles: RowTitleProps[] = [
  { title: 'Transfer Number' },
  { title: 'SKU Code' },
  { title: 'Start Date', sortField: 'start_transfer_at' },
  { title: 'Status' },
  { title: 'User' },
  { title: 'Flag Reason' },
];

const updatedAtRowTitle: RowTitleProps = {
  title: 'Updated At',
  sortField: 'updatedAt',
};

const defaultSort: DefaultSortProps = {
  sortField: 'start_transfer_at',
  sortOrder: SortOrder.DESC,
};

const cancelledDefaultSort: DefaultSortProps = {
  sortField: 'updatedAt',
  sortOrder: SortOrder.DESC,
};

const Row: React.FC<{
  data: IStockTransfer;
  onCheckboxClick: (skuCode: string) => void;
  checked: boolean;
  selectedLocation?: number;
  onClickDetail: (stockTransfer: IStockTransfer) => void;
  showUpdatedAt?: boolean;
}> = ({
  data,
  onCheckboxClick,
  checked,
  selectedLocation,
  onClickDetail,
  showUpdatedAt,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] = useState<IOpenDialogData | null>(
    null,
  );

  const handleCloseDialog = (): void => {
    setOpenDialogData(null);
    setOpen(false);
  };

  return (
    <>
      <S.TableRow>
        <S.TableColumn>
          <Checkbox
            onClick={() => onCheckboxClick(data.transfer_number)}
            checked={checked}
          />
        </S.TableColumn>
        <S.TableColumn>{data.transfer_number}</S.TableColumn>
        <S.TableColumn>{data.sku_code}</S.TableColumn>
        <S.TableColumn>
          {data.start_transfer_at
            ? new Date(data.start_transfer_at).toLocaleString('id-ID')
            : null}
        </S.TableColumn>
        <S.TableColumn style={{ textTransform: 'capitalize' }}>
          {capitalize(data.status.replace('-', ' '))}
        </S.TableColumn>
        <S.TableColumn style={{ width: '15%' }}>{data.user}</S.TableColumn>
        <S.TableColumn style={{ width: '20%' }}>{data.reason}</S.TableColumn>
        {showUpdatedAt && (
          <S.TableColumn>
            {data.updatedAt
              ? new Date(data.updatedAt).toLocaleString('id-ID')
              : null}
          </S.TableColumn>
        )}
        <S.TableColumn style={{ textAlign: 'right' }}>
          <Button onClick={() => onClickDetail(data)} variant="outlined">
            Detail
          </Button>
        </S.TableColumn>
      </S.TableRow>
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(data.transfer_number) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            data.transfer_number,
            selectedLocation,
          )
        }
      />
    </>
  );
};

const StockTransferTable: React.FC<StockTransferDataTableProps> = (
  params,
): React.ReactNode => {
  const { data = [], showUpdatedAt = false }: StockTransferDataTableProps =
    params;
  const dispatch = useDispatch<AppDispatch>();

  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedTasks, setCheckedTasks] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] =
    useState<IOpenBulkDialogData | null>(null);
  const [detailStockTransfer, setDetailStockTransfer] =
    useState<IStockTransfer | null>(null);

  const selectedTasks = data.filter((task) =>
    checkedTasks.includes(task.transfer_number),
  );

  const { selectedLocation } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  const selectedStockTransferNumbers = selectedTasks.map(
    (task) => task.transfer_number,
  );

  const handleCheckTask = (stockTransferNumber: string): void => {
    if (checkedTasks.includes(stockTransferNumber)) {
      // remove skuCode from checkedTasks
      checkedTasks.splice(checkedTasks.indexOf(stockTransferNumber), 1);
      setCheckedTasks([...checkedTasks]);
    } else {
      // insert stock transfer number to checkedOrders
      setCheckedTasks([...checkedTasks, stockTransferNumber]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      // remove all check list
      setCheckedTasks([]);
    } else {
      // add all sku code to checkedTasks
      setCheckedTasks(data.map((task) => task.transfer_number));
    }
    setCheckAll(!checkAll);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleClickMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (name: string): void => {
    setOpenDialogData(OpenBulkDialogConfig[name]);
    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpenDialogData(null);
    setOpen(false);
  };

  const handleOpenDetailDialog = (stockTransfer: IStockTransfer): void => {
    setDetailStockTransfer(stockTransfer);
  };

  const handleCloseDetailDialog = (): void => {
    setDetailStockTransfer(null);
  };

  return (
    <>
      <TableActionRow
        dataName="bin transfer"
        selectedData={selectedTasks}
        anchorEl={anchorEl}
        openMenu={openMenu}
        handleClickMenu={handleClickMenu}
      >
        <ActionBtnMenuStockTransfer
          selectedTask={selectedTasks}
          handleCloseMenu={handleCloseMenu}
          handleOpen={handleOpenDialog}
        />
      </TableActionRow>
      <DataTable
        key={showUpdatedAt ? 'cancelled' : 'default'}
        handleCheckAll={handleCheckAll}
        checkAll={checkAll}
        rowTitles={
          showUpdatedAt ? [...rowTitles, updatedAtRowTitle] : rowTitles
        }
        defaultSort={showUpdatedAt ? cancelledDefaultSort : defaultSort}
      >
        <S.TableBody>
          {data.map((item) => (
            <Row
              data={item}
              key={item.transfer_number}
              onCheckboxClick={handleCheckTask}
              checked={checkedTasks.includes(item.transfer_number)}
              selectedLocation={selectedLocation?.warehouse_id}
              onClickDetail={handleOpenDetailDialog}
              showUpdatedAt={showUpdatedAt}
            />
          ))}
        </S.TableBody>
      </DataTable>
      {data.length > 0 && (
        <TableActionRow
          dataName="bin transfer"
          selectedData={selectedTasks}
          anchorEl={anchorEl}
          openMenu={openMenu}
          handleClickMenu={handleClickMenu}
        >
          <ActionBtnMenuStockTransfer
            selectedTask={selectedTasks}
            handleCloseMenu={handleCloseMenu}
            handleOpen={handleOpenDialog}
          />
        </TableActionRow>
      )}
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(selectedStockTransferNumbers) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            selectedStockTransferNumbers,
            selectedLocation?.warehouse_id,
          )
        }
      />
      <StockTransferDetailDialog
        open={!!detailStockTransfer}
        stockTransfer={detailStockTransfer}
        onClose={handleCloseDetailDialog}
      />
    </>
  );
};

export default StockTransferTable;
